import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { get, post, setCsrfToken, setUnauthenticatedHandler } from './api.ts';

export interface Me {
  user: { id: string; email: string; displayName: string; isPlatformAdmin: boolean };
  csrfToken: string;
  workspaces: Array<{ id: string; name: string; role: 'owner' | 'operator' | 'viewer' }>;
  appName: string;
  features: { sms: boolean };
}

interface SessionValue {
  me: Me | null;
  loading: boolean;
  workspaceId: string | null;
  role: 'owner' | 'operator' | 'viewer' | null;
  refresh: () => Promise<Me | null>;
  signOut: () => Promise<void>;
  setWorkspace: (id: string) => void;
  can: (need: 'owner' | 'operator' | 'viewer') => boolean;
}

const Ctx = createContext<SessionValue | null>(null);
const RANK = { viewer: 1, operator: 2, owner: 3 } as const;
const WS_KEY = 'bs.workspace';

function storedWorkspace(): string | null {
  try {
    return localStorage.getItem(WS_KEY);
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState<string | null>(storedWorkspace());

  const refresh = useCallback(async () => {
    try {
      const m = await get<Me>('/api/auth/me');
      setCsrfToken(m.csrfToken);
      setMe(m);
      setWorkspaceId((cur) =>
        cur && m.workspaces.some((w) => w.id === cur) ? cur : (m.workspaces[0]?.id ?? null),
      );
      return m;
    } catch {
      setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    setUnauthenticatedHandler(() => setMe(null));
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await post('/api/auth/logout');
    } finally {
      setMe(null);
      setCsrfToken('');
    }
  }, []);

  const setWorkspace = useCallback((id: string) => {
    setWorkspaceId(id);
    try {
      localStorage.setItem(WS_KEY, id);
    } catch {
      /* optional convenience only */
    }
  }, []);

  const role = me?.workspaces.find((w) => w.id === workspaceId)?.role ?? null;
  const value = useMemo<SessionValue>(
    () => ({
      me,
      loading,
      workspaceId,
      role,
      refresh,
      signOut,
      setWorkspace,
      can: (need) => !!role && RANK[role] >= RANK[need],
    }),
    [me, loading, workspaceId, role, refresh, signOut, setWorkspace],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

/** Base path for the active workspace's API. */
export function useWs(): string {
  const { workspaceId } = useSession();
  return `/api/w/${workspaceId ?? 'none'}`;
}
